import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight, Home } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { fetchNewsArticle, fetchNewsList, mediaUrl } from '../api/news';

const NewsArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    let alive = true;
    setLoading(true);
    setNotFound(false);
    Promise.all([fetchNewsArticle(slug), fetchNewsList()])
      .then(([art, list]) => {
        if (!alive) return;
        setArticle(art);
        setNews(list);
      })
      .catch((e) => {
        if (!alive) return;
        if (e.status === 404) setNotFound(true);
        else setNotFound(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  const idx = news.findIndex((n) => n.slug === slug);
  const related = news.filter((n) => n.slug !== slug).slice(0, 3);
  const prev = idx > 0 ? news[idx - 1] : null;
  const next = idx >= 0 && idx < news.length - 1 ? news[idx + 1] : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-[#555]">
        <p style={{ fontFamily: "'Montserrat', sans-serif" }}>Carregando matéria…</p>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0e0e0e] text-white px-6">
        <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Matéria não encontrada
        </h1>
        <button
          onClick={() => navigate('/news')}
          className="mt-4 px-6 py-3 bg-[#C99A36] text-black font-semibold tracking-[0.15em] rounded-full hover:bg-[#E8C16A] transition-colors"
        >
          Ver todas as matérias
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Header />

      <section className="relative mt-[80px] w-full h-[50vh] min-h-[380px] overflow-hidden">
        <img
          src={mediaUrl(article.image)}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/80" />

        <div className="relative z-10 h-full flex flex-col items-center justify-end text-center px-6 pb-14 max-w-5xl mx-auto">
          <p
            className="text-[#E8C16A] tracking-[0.4em] text-[11px] md:text-[12px] font-semibold mb-4 uppercase"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            SETA NEWS
          </p>
          <h1
            className="text-white text-[26px] md:text-[38px] lg:text-[46px] font-black tracking-tight leading-[1.1] max-w-4xl uppercase drop-shadow-lg"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {article.title}
          </h1>
          <div
            className="mt-6 flex items-center gap-6 text-white/85 text-[12px] tracking-wide"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            <span className="flex items-center gap-2">
              <Calendar size={14} /> {article.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock size={14} /> {article.readTime}
            </span>
          </div>
        </div>
      </section>

      <div className="bg-[#0e0e0e] border-t border-white/5">
        <div
          className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3 text-[11px] tracking-[0.15em] uppercase text-white/60"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          <Link to="/" className="hover:text-[#E8C16A] transition-colors flex items-center gap-1">
            <Home size={12} /> Início
          </Link>
          <span>/</span>
          <Link to="/news" className="hover:text-[#E8C16A] transition-colors">
            SETA News
          </Link>
          <span>/</span>
          <span className="text-white/85 truncate">{article.title}</span>
        </div>
      </div>

      <article className="bg-white py-20 px-6">
        <div className="max-w-3xl mx-auto">
          {(article.body || []).map((block, i) => {
            if (block.type === 'p') {
              return (
                <p
                  key={i}
                  className="text-[#2a2a2a] text-[16px] md:text-[17px] leading-[1.9] tracking-wide text-justify mb-7"
                  style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 400 }}
                >
                  {block.text}
                </p>
              );
            }
            if (block.type === 'image') {
              return (
                <figure key={i} className="my-10">
                  <img
                    src={mediaUrl(block.src)}
                    alt={block.alt || article.title}
                    loading="lazy"
                    className="w-full h-auto shadow-lg"
                  />
                  {block.alt && (
                    <figcaption
                      className="mt-3 text-center text-[12px] text-[#888] italic"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {block.alt}
                    </figcaption>
                  )}
                </figure>
              );
            }
            return null;
          })}

          <div className="mt-16 pt-10 border-t border-neutral-200 grid grid-cols-1 md:grid-cols-2 gap-4">
            {prev ? (
              <Link
                to={`/news/${prev.slug}`}
                className="group flex items-center gap-3 p-5 bg-[#f5f5f3] hover:bg-[#0e0e0e] transition-colors duration-300"
              >
                <ArrowLeft
                  size={20}
                  className="text-[#C99A36] group-hover:-translate-x-1 transition-transform flex-shrink-0"
                />
                <span className="flex flex-col">
                  <span
                    className="text-[10px] tracking-[0.2em] text-[#888] group-hover:text-[#E8C16A] uppercase font-semibold"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Anterior
                  </span>
                  <span
                    className="text-[13px] font-semibold text-[#1a1a1a] group-hover:text-white line-clamp-2"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {prev.title}
                  </span>
                </span>
              </Link>
            ) : (
              <div />
            )}

            {next ? (
              <Link
                to={`/news/${next.slug}`}
                className="group flex items-center gap-3 p-5 bg-[#f5f5f3] hover:bg-[#0e0e0e] transition-colors duration-300 md:text-right md:flex-row-reverse"
              >
                <ArrowRight
                  size={20}
                  className="text-[#C99A36] group-hover:translate-x-1 transition-transform flex-shrink-0"
                />
                <span className="flex flex-col md:items-end">
                  <span
                    className="text-[10px] tracking-[0.2em] text-[#888] group-hover:text-[#E8C16A] uppercase font-semibold"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Próximo
                  </span>
                  <span
                    className="text-[13px] font-semibold text-[#1a1a1a] group-hover:text-white line-clamp-2"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {next.title}
                  </span>
                </span>
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </article>

      <section className="bg-[#f5f5f3] py-20 px-6">
        <div className="max-w-[1300px] mx-auto">
          <div className="text-center mb-12">
            <p
              className="text-[#C99A36] tracking-[0.4em] text-[12px] font-semibold mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              LEIA TAMBÉM
            </p>
            <h2
              className="text-[#1a1a1a] text-[28px] md:text-[34px] font-bold tracking-tight"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Outras Matérias
            </h2>
            <div className="mx-auto mt-4 w-24 h-[3px] bg-[#C99A36]" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {related.map((n) => (
              <Link
                key={n.id}
                to={`/news/${n.slug}`}
                className="group bg-white shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden block"
              >
                <div className="overflow-hidden h-[200px]">
                  <img
                    src={mediaUrl(n.image)}
                    alt={n.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <h3
                    className="text-[#1a1a1a] font-bold text-[15px] tracking-wide mb-2 group-hover:text-[#C99A36] transition-colors line-clamp-2"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {n.title}
                  </h3>
                  <div
                    className="flex items-center gap-3 text-[11px] text-[#888] tracking-wide"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    <span className="flex items-center gap-1">
                      <Calendar size={11} /> {n.date}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#1a1a1a] text-white text-[12px] tracking-[0.25em] font-semibold hover:bg-[#C99A36] transition-colors duration-300"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              VER TODAS AS MATÉRIAS <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsArticlePage;
